import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { SocialFeed } from '@/components/social/SocialFeed';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { supabase } from '@/integrations/supabase/client';
import { ProfilePersonalizationWizard } from '@/components/onboarding/ProfilePersonalizationWizard';

const Dashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    async function checkProfile() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          
          setProfile(data);
          
          // Check if wizard should be shown (from sessionStorage or first time in session)
          const shouldShowWizard = sessionStorage.getItem('showWizard') === 'true';
          const hasShownWizardThisSession = sessionStorage.getItem('wizardShownThisSession') === 'true';
          
          if (!hasShownWizardThisSession && data && !data.optional_fields_completed && (data.wizard_views ?? 0) < 3) {
            setShowWizard(true);
            // Mark that wizard was shown this session
            sessionStorage.setItem('wizardShownThisSession', 'true');
            // Clear the flag from AuthCallback
            sessionStorage.removeItem('showWizard');
          }
        }
      } catch (error) {
        console.error('Error checking profile:', error);
      } finally {
        setLoading(false);
      }
    }
    checkProfile();
  }, []);

  // Redirect to Home after profile check
  useEffect(() => {
    if (!loading && profile) {
      navigate('/home');
    }
  }, [loading, profile, navigate]);

  const handleWizardComplete = async (completed: boolean) => {
    setShowWizard(false);
    if (profile) {
      const newViews = (profile.wizard_views ?? 0) + 1;
      const shouldComplete = completed || newViews >= 3;
      await supabase
        .from('profiles')
        .update({
          wizard_views: newViews,
          optional_fields_completed: shouldComplete,
        })
        .eq('id', profile.id);
      // Atualiza o perfil local
      setProfile((prev: any) => ({
        ...prev,
        wizard_views: newViews,
        optional_fields_completed: shouldComplete,
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white text-lg">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen pb-20" style={{ background: '#160C0C' }}>
      <TopNavigation variant="subtle" />
      
      <main className="pt-16 px-5">
        <div className="py-6">
          <SocialFeed />
        </div>
      </main>

      <BottomNavigation />
      
      {/* Wizard Modal */}
      {showWizard && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl shadow-lg w-[80vw] max-w-[700px] p-0">
            <ProfilePersonalizationWizard
              onComplete={handleWizardComplete}
              profile={profile}
              setProfile={setProfile}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
